import type { TopicDrilldownStats } from "@repo/app-types/entities/topic";
import type { FC, ReactNode } from "react";
import {
  DeltaPill,
  type DeltaTone,
} from "../../teammatePerformance/monthInReview/deltaPill.js";
import { SignedImpactCell } from "../signedImpactCell.js";

interface TopicSummaryStripProps {
  current: TopicDrilldownStats;
  winRateImpact: number | null;
  /**
   * Signed-pts delta vs. the scope-wide baseline — not the 1-5 average
   * the breakdown card shows.
   */
  customerExperienceScoreImpact: number | null;
}

/**
 * "Overview" labeled-row list inside the slideover: one row per metric
 * with the value right-aligned.
 */
export const TopicSummaryStrip: FC<TopicSummaryStripProps> = ({
  current,
  winRateImpact,
  customerExperienceScoreImpact,
}) => {
  const matchesDelta = current.matchCount - current.priorPeriodMatchCount;
  const tone: DeltaTone =
    matchesDelta > 0 ? "positive" : matchesDelta < 0 ? "negative" : "neutral";
  const deltaLabel =
    matchesDelta === 0
      ? "0"
      : `${matchesDelta > 0 ? "+" : "−"}${Math.abs(matchesDelta)}`;

  return (
    <dl className="divide-y divide-gray-100 rounded-lg border border-border-light bg-white">
      <Row
        label="Volume"
        value={`${current.outcomeCorrelation?.nWithTopic ?? 0} calls`}
      />
      <Row
        label="Volume trend"
        value={<DeltaPill delta={deltaLabel} tone={tone} />}
      />
      <Row
        label="Win impact"
        value={
          <SignedImpactCell value={winRateImpact} unit="pp" decimals={1} />
        }
      />
      <Row
        label="Experience score"
        value={
          <SignedImpactCell value={customerExperienceScoreImpact} unit="pts" />
        }
      />
    </dl>
  );
};

const Row: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
  <div className="flex items-center justify-between px-4 py-3 text-sm">
    <dt className="text-gray-600">{label}</dt>
    <dd className="font-semibold text-foreground">{value}</dd>
  </div>
);
